import * as React from 'react';
import {
    Alert,
    AlertTitle,
    Avatar,
    Box,
    Button,
    Checkbox,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControlLabel,
    IconButton,
    LinearProgress,
    MenuItem,
    Paper,
    Select,
    Stack,
    Step,
    StepLabel,
    Stepper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    ToggleButton,
    ToggleButtonGroup,
    Tooltip,
    Typography
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DownloadIcon from '@mui/icons-material/Download';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ReplayIcon from '@mui/icons-material/Replay';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import ChangeCircleOutlinedIcon from '@mui/icons-material/ChangeCircleOutlined';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import xslxSheetIcon from '../../assets/images';
import type {
    ImportAdapter,
    ImportCommitOutcome,
    ImportExecutionResult,
    ImportRowAction,
    ImportRowPreview
} from './types';
import { downloadImportTemplate, readSpreadsheetRows } from './parsing';
import { getCell, toInt } from 'api/customer/xlsxImportShared';
import { findMappingConflicts, HeaderMatchConfidence, suggestColumnMapping } from './headerMatching';
import { analyzeImportRows } from './analyzeRows';

const STEP_LABELS = ['העלאת קובץ', 'התאמת עמודות', 'בדיקה ואישור', 'תוצאות'];

const ACTION_META: Record<ImportRowAction, { label: string; color: 'success' | 'info' | 'default' | 'error' | 'warning'; icon: React.ReactNode }> = {
    create: { label: 'חדש', color: 'success', icon: <AddCircleOutlineIcon fontSize="small" /> },
    update: { label: 'עדכון', color: 'warning', icon: <ChangeCircleOutlinedIcon fontSize="small" /> },
    unchanged: { label: 'ללא שינוי', color: 'default', icon: <RemoveCircleOutlineIcon fontSize="small" /> },
    invalid: { label: 'שגיאה', color: 'error', icon: <ErrorIcon fontSize="small" /> },
    duplicate: { label: 'כפילות בקובץ', color: 'error', icon: <WarningAmberIcon fontSize="small" /> }
};

const CONFIDENCE_META: Record<HeaderMatchConfidence, { label: string; color: 'success' | 'info' | 'warning' | 'default' }> = {
    exact: { label: 'התאמה מדויקת', color: 'success' },
    alias: { label: 'התאמה מוכרת', color: 'info' },
    fuzzy: { label: 'התאמה חלקית — בדוק', color: 'warning' },
    none: { label: 'לא נמצא — יש לבחור', color: 'default' }
};

function csvEscape(v: unknown): string {
    const s = String(v ?? '');
    if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
    return s;
}

function downloadResultsCsv(results: ImportExecutionResult[], fileName: string) {
    const header = ['שורה', 'פעולה', 'סטטוס', 'הודעה'];
    const lines = [header.join(','), ...results.map((r) => [r.line, ACTION_META[r.action]?.label ?? r.action, r.status, r.message].map(csvEscape).join(','))];
    const blob = new Blob(['\uFEFF' + lines.join('\r\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    a.click();
    URL.revokeObjectURL(url);
}

export interface ImportWizardDialogProps<TEntity> {
    open: boolean;
    onClose: () => void;
    adapter: ImportAdapter<TEntity>;
    currentData: TEntity[];
    /** Called once after the user closes the results step, typically to refetch the list. */
    onFinished?: () => void;
}

type ColumnMap = Record<string, number | undefined>;

export default function ImportWizardDialog<TEntity>({ open, onClose, adapter, currentData, onFinished }: ImportWizardDialogProps<TEntity>) {
    const [step, setStep] = React.useState(0);
    const [file, setFile] = React.useState<File | undefined>();
    const [rawRows, setRawRows] = React.useState<unknown[][]>([]);
    const [parseError, setParseError] = React.useState<string | undefined>();
    const [assumeExtraTitleRow, setAssumeExtraTitleRow] = React.useState(!!adapter.defaultAssumeExtraTitleRow);
    const [mapping, setMapping] = React.useState<ColumnMap>({});
    const [ignoreMissingRequired, setIgnoreMissingRequired] = React.useState(false);
    const [confidences, setConfidences] = React.useState<Record<string, HeaderMatchConfidence>>({});

    const [rows, setRows] = React.useState<ImportRowPreview<TEntity>[]>([]);
    const [truncated, setTruncated] = React.useState(false);
    const [filter, setFilter] = React.useState<ImportRowAction | 'all'>('all');
    const [approved, setApproved] = React.useState(false);
    const [reviewOptionValues, setReviewOptionValues] = React.useState<Record<string, boolean>>(() =>
        Object.fromEntries((adapter.reviewOptions ?? []).map((o) => [o.key, !!o.defaultChecked]))
    );

    const [executing, setExecuting] = React.useState(false);
    const [results, setResults] = React.useState<ImportExecutionResult[]>([]);
    const [progress, setProgress] = React.useState(0);

    const inputRef = React.useRef<HTMLInputElement | null>(null);
    const workingDataRef = React.useRef<TEntity[]>(currentData);
    React.useEffect(() => {
        workingDataRef.current = currentData;
    }, [currentData]);

    const headerRow = React.useMemo(() => (assumeExtraTitleRow ? rawRows[1] : rawRows[0]) ?? [], [rawRows, assumeExtraTitleRow]);

    const resetAll = React.useCallback(() => {
        setStep(0);
        setFile(undefined);
        setRawRows([]);
        setParseError(undefined);
        setMapping({});
        setConfidences({});
        setIgnoreMissingRequired(false);
        setRows([]);
        setTruncated(false);
        setFilter('all');
        setApproved(false);
        setReviewOptionValues(
            Object.fromEntries((adapter.reviewOptions ?? []).map((o) => [o.key, !!o.defaultChecked]))
        );
        setExecuting(false);
        setResults([]);
        setProgress(0);
    }, [adapter]);

    const handleClose = () => {
        if (executing) return;
        resetAll();
        onClose();
    };

    const handleFinishedAndClose = () => {
        resetAll();
        onClose();
        onFinished?.();
    };

    const handleFileSelected = async (f: File) => {
        setParseError(undefined);
        try {
            const parsed = await readSpreadsheetRows(f);
            if (!parsed || parsed.length === 0) {
                setParseError('לא נמצאו נתונים בקובץ. בדוק שהקובץ אינו ריק ושהוא בפורמט xlsx/xls/csv.');
                setFile(undefined);
                setRawRows([]);
                return;
            }
            setFile(f);
            setRawRows(parsed);
            const headers = (assumeExtraTitleRow ? parsed[1] : parsed[0]) ?? [];
            const suggestions = suggestColumnMapping(adapter.columns, headers);
            const nextMapping: ColumnMap = {};
            const nextConfidence: Record<string, HeaderMatchConfidence> = {};
            adapter.columns.forEach((c) => {
                nextMapping[c.key] = suggestions[c.key]?.columnIndex;
                nextConfidence[c.key] = suggestions[c.key]?.confidence ?? 'none';
            });
            setMapping(nextMapping);
            setConfidences(nextConfidence);
        } catch (e) {
            setParseError(`לא ניתן לקרוא את הקובץ: ${(e as Error)?.message || String(e)}`);
            setFile(undefined);
            setRawRows([]);
        }
    };

    const onFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const f = e.target.files?.[0];
        if (f) void handleFileSelected(f);
    };

    const onDrop = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        const f = e.dataTransfer.files?.[0];
        if (f) void handleFileSelected(f);
    };

    // --- Mapping step ---
    const mappingConflicts = React.useMemo(() => findMappingConflicts(mapping), [mapping]);
    const missingRequired = React.useMemo(
        () => adapter.columns.filter((c) => c.required && mapping[c.key] == null),
        [adapter.columns, mapping]
    );
    const canProceedFromMapping = mappingConflicts.length === 0 && (ignoreMissingRequired || missingRequired.length === 0);

    const runAnalysis = React.useCallback(() => {
        const { rows: built, truncated: didTruncate } = analyzeImportRows(adapter, rawRows, mapping, assumeExtraTitleRow, currentData);
        setRows(built);
        setTruncated(didTruncate);
        setApproved(false);
        setFilter('all');
    }, [adapter, mapping, rawRows, assumeExtraTitleRow, currentData]);

    const goToReview = () => {
        runAnalysis();
        setStep(2);
    };

    // --- Review step ---
    const counts = React.useMemo(() => {
        const c: Record<ImportRowAction, number> = { create: 0, update: 0, unchanged: 0, invalid: 0, duplicate: 0 };
        rows.forEach((r) => {
            c[r.action] += 1;
        });
        return c;
    }, [rows]);

    const selectedCount = React.useMemo(() => rows.filter((r) => r.selected).length, [rows]);
    const foundCount = React.useMemo(
        () => rows.filter((r) => r.existing != null && r.action !== 'invalid' && r.action !== 'duplicate').length,
        [rows]
    );
    const extraActionReady = Object.values(reviewOptionValues).some(Boolean) && foundCount > 0;
    const canRun = selectedCount > 0 || extraActionReady;
    const visibleRows = React.useMemo(() => (filter === 'all' ? rows : rows.filter((r) => r.action === filter)), [rows, filter]);

    const toggleRow = (rowIndex: number) => {
        setRows((prev) => prev.map((r) => (r.rowIndex === rowIndex ? { ...r, selected: !r.selected } : r)));
    };

    const setGroupSelection = (action: ImportRowAction | 'all', value: boolean) => {
        setRows((prev) =>
            prev.map((r) => {
                if (action !== 'all' && r.action !== action) return r;
                if (r.action === 'invalid' || r.action === 'duplicate') return r;
                return { ...r, selected: value };
            })
        );
    };

    // --- Execute step ---
    const applyUpdatedEntity = (updated?: TEntity) => {
        if (!updated) return;
        const withId = updated as unknown as { id?: unknown };
        if (withId?.id == null) return;
        const list = workingDataRef.current;
        const idx = list.findIndex((x) => (x as unknown as { id?: unknown }).id === withId.id);
        workingDataRef.current = idx >= 0 ? [...list.slice(0, idx), updated, ...list.slice(idx + 1)] : [...list, updated];
    };

    const runExecution = async (rowsToRun: ImportRowPreview<TEntity>[]) => {
        setExecuting(true);
        setStep(3);
        const nextResults: ImportExecutionResult[] = [...results.filter((r) => !rowsToRun.some((x) => x.line === r.line))];
        setResults(nextResults);
        setProgress(0);

        if (adapter.commitBatch) {
            try {
                const outcomes = await adapter.commitBatch(rowsToRun, {
                    currentData: workingDataRef.current,
                    options: reviewOptionValues,
                    allRows: rows
                });
                rowsToRun.forEach((r, i) => {
                    const outcome: ImportCommitOutcome<TEntity> | undefined = outcomes[i];
                    applyUpdatedEntity(outcome?.updatedEntity);
                    nextResults.push({
                        line: r.line,
                        label: r.label,
                        action: r.action,
                        status: outcome?.ok ? 'success' : 'error',
                        message: outcome?.message ?? 'לא התקבלה תשובה'
                    });
                });
            } catch (e) {
                rowsToRun.forEach((r) => {
                    nextResults.push({ line: r.line, label: r.label, action: r.action, status: 'error', message: (e as Error)?.message || String(e) });
                });
            }
            setResults([...nextResults]);
            setProgress(rowsToRun.length);
        } else {
            for (let i = 0; i < rowsToRun.length; i++) {
                const r = rowsToRun[i];
                try {
                    let outcome: ImportCommitOutcome<TEntity> | undefined;
                    if (r.action === 'create' && adapter.commitCreate) {
                        outcome = await adapter.commitCreate(r.patch, {
                            currentData: workingDataRef.current,
                            options: reviewOptionValues
                        });
                    } else if (r.action === 'update' && r.existing && adapter.commitUpdate) {
                        outcome = await adapter.commitUpdate(r.existing, r.patch, {
                            currentData: workingDataRef.current,
                            options: reviewOptionValues
                        });
                    } else {
                        outcome = { ok: false, message: 'לא ניתן לבצע שורה זו' };
                    }
                    applyUpdatedEntity(outcome?.updatedEntity);
                    nextResults.push({ line: r.line, label: r.label, action: r.action, status: outcome?.ok ? 'success' : 'error', message: outcome?.message ?? '' });
                } catch (e) {
                    nextResults.push({ line: r.line, label: r.label, action: r.action, status: 'error', message: (e as Error)?.message || String(e) });
                }
                setProgress(i + 1);
                setResults([...nextResults]);
            }
        }
        setExecuting(false);
    };

    const handleApproveAndRun = () => {
        let toRun = rows.filter((r) => r.selected);
        if (toRun.length === 0 && extraActionReady) {
            toRun = rows.filter((r) => r.existing != null && r.action !== 'invalid' && r.action !== 'duplicate');
        }
        if (toRun.length === 0) return;
        void runExecution(toRun);
    };

    const handleRetryFailed = () => {
        const failedLines = new Set(results.filter((r) => r.status === 'error').map((r) => r.line));
        const toRetry = rows.filter((r) => failedLines.has(r.line));
        void runExecution(toRetry);
    };

    const successCount = results.filter((r) => r.status === 'success').length;
    const errorCount = results.filter((r) => r.status === 'error').length;

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span>{adapter.title}</span>
                <IconButton onClick={handleClose} disabled={executing} size="small">
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent>
                <Stepper activeStep={step} sx={{ mb: 3 }} alternativeLabel>
                    {STEP_LABELS.map((label) => (
                        <Step key={label}>
                            <StepLabel>{label}</StepLabel>
                        </Step>
                    ))}
                </Stepper>

                {step === 0 && (
                    <Box>
                        {adapter.instructions?.length ? (
                            <Alert severity="info" sx={{ mb: 2 }}>
                                <AlertTitle>איך זה עובד</AlertTitle>
                                <ul style={{ margin: 0, paddingInlineStart: 20 }}>
                                    {adapter.instructions.map((line, i) => (
                                        <li key={i}>{line}</li>
                                    ))}
                                </ul>
                            </Alert>
                        ) : null}

                        <Alert severity="info" sx={{ mb: 2 }}>
                            לא בטוחים איך להכין את הקובץ? הורידו קובץ דוגמה עם כל העמודות הנתמכות ושורת דוגמה, ומלאו אותו.
                            <Box sx={{ mt: 1 }}>
                                <Button
                                    size="small"
                                    variant="outlined"
                                    startIcon={<DownloadIcon />}
                                    onClick={() => downloadImportTemplate(adapter.columns, `דוגמה - ${adapter.title}.xlsx`)}
                                >
                                    הורדת קובץ דוגמה
                                </Button>
                            </Box>
                        </Alert>

                        <Paper
                            variant="outlined"
                            onDragOver={(e) => e.preventDefault()}
                            onDrop={onDrop}
                            sx={{
                                p: 4,
                                textAlign: 'center',
                                borderStyle: 'dashed',
                                cursor: 'pointer',
                                bgcolor: file ? 'action.hover' : undefined
                            }}
                            onClick={() => inputRef.current?.click()}
                        >
                            <input ref={inputRef} type="file" accept=".xlsx,.xls,.csv,.txt" style={{ display: 'none' }} onChange={onFileInputChange} />
                            <Avatar variant="square" src={xslxSheetIcon} alt="Excel" sx={{ width: 40, height: 40, mx: 'auto', mb: 1, bgcolor: 'transparent', '& img': { objectFit: 'contain' } }} />
                            <Typography>{file ? file.name : 'גררו לכאן קובץ אקסל/CSV או לחצו לבחירה'}</Typography>
                            {file ? (
                                <Typography variant="body2" color="text.secondary">
                                    {rawRows.length} שורות נמצאו בקובץ
                                </Typography>
                            ) : null}
                        </Paper>

                        {parseError ? (
                            <Alert severity="error" sx={{ mt: 2 }}>
                                {parseError}
                            </Alert>
                        ) : null}

                        <FormControlLabel
                            sx={{ mt: 2 }}
                            control={
                                <Checkbox
                                    checked={assumeExtraTitleRow}
                                    onChange={(e) => {
                                        setAssumeExtraTitleRow(e.target.checked);
                                        if (rawRows.length) {
                                            const headers = (e.target.checked ? rawRows[1] : rawRows[0]) ?? [];
                                            const suggestions = suggestColumnMapping(adapter.columns, headers);
                                            const nextMapping: ColumnMap = {};
                                            const nextConfidence: Record<string, HeaderMatchConfidence> = {};
                                            adapter.columns.forEach((c) => {
                                                nextMapping[c.key] = suggestions[c.key]?.columnIndex;
                                                nextConfidence[c.key] = suggestions[c.key]?.confidence ?? 'none';
                                            });
                                            setMapping(nextMapping);
                                            setConfidences(nextConfidence);
                                        }
                                    }}
                                />
                            }
                            label="יש שורת כותרת נוספת מעל שמות העמודות (שורה 1 היא כותרת כללית, שמות העמודות בשורה 2)"
                        />
                    </Box>
                )}

                {step === 1 && (
                    <Box>
                        <Typography variant="body2" sx={{ mb: 2 }} color="text.secondary">
                            זוהו אוטומטית העמודות המתאימות ביותר מהקובץ. בדקו שההתאמה נכונה, ותקנו במידת הצורך.
                        </Typography>
                        {mappingConflicts.length > 0 ? (
                            <Alert severity="error" sx={{ mb: 2 }}>
                                לא ניתן למפות שתי עמודות שונות לאותו טור בקובץ. בדקו את המיפוי של:{' '}
                                {mappingConflicts
                                    .map((c) => c.keys.map((k) => adapter.columns.find((col) => col.key === k)?.label ?? k).join(' / '))
                                    .join(', ')}
                            </Alert>
                        ) : null}
                        {missingRequired.length > 0 && !ignoreMissingRequired ? (
                            <Alert severity="warning" sx={{ mb: 2 }}>
                                עמודות חובה שלא זוהו: {missingRequired.map((c) => c.label).join(', ')}
                            </Alert>
                        ) : null}
                        <TableContainer component={Paper} variant="outlined">
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>עמודה ביישום</TableCell>
                                        <TableCell>עמודה בקובץ</TableCell>
                                        <TableCell>סוג</TableCell>
                                        <TableCell>התאמה</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {adapter.columns.map((c) => {
                                        const conf = confidences[c.key] ?? 'none';
                                        const confMeta = CONFIDENCE_META[conf];
                                        return (
                                            <TableRow key={c.key}>
                                                <TableCell>
                                                    {c.label} {c.required ? <Chip size="small" color="primary" label="חובה" sx={{ ml: 1 }} /> : null}
                                                    {c.hint ? (
                                                        <Typography variant="caption" color="text.secondary" display="block">
                                                            {c.hint}
                                                        </Typography>
                                                    ) : null}
                                                </TableCell>
                                                <TableCell>
                                                    <Select
                                                        size="small"
                                                        fullWidth
                                                        value={mapping[c.key] ?? ''}
                                                        displayEmpty
                                                        onChange={(e) => {
                                                            const v = e.target.value === '' ? undefined : Number(e.target.value);
                                                            setMapping((m) => ({ ...m, [c.key]: v }));
                                                            setConfidences((cf) => ({ ...cf, [c.key]: 'exact' }));
                                                        }}
                                                    >
                                                        <MenuItem value="">
                                                            <em>לא בשימוש</em>
                                                        </MenuItem>
                                                        {headerRow.map((h, i) => (
                                                            <MenuItem key={i} value={i}>
                                                                {String(h ?? `עמודה ${i + 1}`)}
                                                            </MenuItem>
                                                        ))}
                                                    </Select>
                                                </TableCell>
                                                <TableCell>{c.type}</TableCell>
                                                <TableCell>
                                                    <Chip size="small" color={confMeta.color} label={confMeta.label} />
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>

                        {missingRequired.length > 0 ? (
                            <FormControlLabel
                                sx={{ mt: 1 }}
                                control={<Checkbox checked={ignoreMissingRequired} onChange={(e) => setIgnoreMissingRequired(e.target.checked)} />}
                                label="המשך בכל זאת בלי עמודות החובה שחסרות (שורות שיחסר בהן מידע נדרש יסומנו כשגויות)"
                            />
                        ) : null}
                    </Box>
                )}

                {step === 2 && (
                    <Box>
                        <Alert severity="warning" sx={{ mb: 2 }}>
                            <AlertTitle>לפני שממשיכים</AlertTitle>
                            בדקו את הרשימה למטה. שורות המסומנות ב<strong>עדכון</strong> ישנו {adapter.entityLabelPlural} קיימים במערכת — ודאו שאלו
                            אכן הרשומות שהתכוונתם לעדכן. הפעולה תבוצע רק על השורות המסומנות בתיבת הסימון.
                        </Alert>

                        <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap' }}>
                            {(['all', 'create', 'update', 'unchanged', 'duplicate', 'invalid'] as const).map((key) => {
                                const count = key === 'all' ? rows.length : counts[key];
                                const label = key === 'all' ? 'הכל' : ACTION_META[key].label;
                                return (
                                    <ToggleButtonGroup key={key} exclusive value={filter} onChange={(_, v) => v && setFilter(v)} size="small">
                                        <ToggleButton value={key} selected={filter === key} disabled={count === 0}>
                                            {label} ({count})
                                        </ToggleButton>
                                    </ToggleButtonGroup>
                                );
                            })}
                        </Stack>

                        <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
                            <Button size="small" onClick={() => setGroupSelection(filter, true)}>
                                בחר הכל בתצוגה זו
                            </Button>
                            <Button size="small" onClick={() => setGroupSelection(filter, false)}>
                                נקה בחירה בתצוגה זו
                            </Button>
                        </Stack>

                        <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 420 }}>
                            <Table size="small" stickyHeader>
                                <TableHead>
                                    <TableRow>
                                        <TableCell padding="checkbox" />
                                        <TableCell>שורה</TableCell>
                                        <TableCell>פעולה</TableCell>
                                        <TableCell>{adapter.entityLabel}</TableCell>
                                        <TableCell>שינויים / הערות</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {visibleRows.map((r) => {
                                        const meta = ACTION_META[r.action];
                                        const blocked = r.action === 'invalid' || r.action === 'duplicate';
                                        return (
                                            <TableRow key={r.rowIndex} hover>
                                                <TableCell padding="checkbox">
                                                    <Checkbox checked={r.selected} disabled={blocked} onChange={() => toggleRow(r.rowIndex)} />
                                                </TableCell>
                                                <TableCell>{r.line}</TableCell>
                                                <TableCell>
                                                    <Chip size="small" color={meta.color} icon={meta.icon as React.ReactElement} label={meta.label} />
                                                </TableCell>
                                                <TableCell>{r.label || <em>—</em>}</TableCell>
                                                <TableCell>
                                                    {r.diffs.length > 0 ? (
                                                        <Stack spacing={0.3}>
                                                            {r.diffs.map((d) => (
                                                                <Typography variant="caption" key={d.key}>
                                                                    <strong>{d.label}:</strong> {String(d.oldValue ?? '—')} → {String(d.newValue ?? '—')}
                                                                </Typography>
                                                            ))}
                                                        </Stack>
                                                    ) : null}
                                                    {r.errors.map((e, i) => (
                                                        <Typography variant="caption" color="error" display="block" key={`e${i}`}>
                                                            {e}
                                                        </Typography>
                                                    ))}
                                                    {r.warnings.map((w, i) => (
                                                        <Typography variant="caption" color="warning.main" display="block" key={`w${i}`}>
                                                            {w}
                                                        </Typography>
                                                    ))}
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>

                        {truncated ? (
                            <Alert severity="warning" sx={{ mt: 2 }}>
                                הקובץ ארוך מהמותר; רק {adapter.maxRows ?? 5000} השורות הראשונות עובדו.
                            </Alert>
                        ) : null}

                        <Divider sx={{ my: 2 }} />
                        <Alert severity={selectedCount === 0 ? 'info' : 'success'}>
                            סה״כ יבוצעו: {counts.create} יצירות ו-{counts.update} עדכונים, מתוך {selectedCount} שורות מסומנות.
                            {counts.duplicate > 0 ? ` ${counts.duplicate} שורות נחסמו כפילות באותו קובץ.` : ''}
                            {counts.invalid > 0 ? ` ${counts.invalid} שורות נחסמו עקב שגיאה.` : ''}
                        </Alert>
                        <FormControlLabel
                            sx={{ mt: 1 }}
                            control={<Checkbox checked={approved} onChange={(e) => setApproved(e.target.checked)} disabled={!canRun} />}
                            label={`אני מאשר לבצע יבוא זה: ${counts.create} יצירות ו-${counts.update} עדכונים. הפעולה שומרת ישירות במערכת ואינה מתבטלת אוטומטית.`}
                        />
                        {(adapter.reviewOptions ?? []).map((opt) => (
                            <FormControlLabel
                                key={opt.key}
                                sx={{ mt: 0.5, display: 'flex' }}
                                control={
                                    <Checkbox
                                        checked={!!reviewOptionValues[opt.key]}
                                        onChange={(e) =>
                                            setReviewOptionValues((prev) => ({ ...prev, [opt.key]: e.target.checked }))
                                        }
                                        disabled={foundCount === 0}
                                    />
                                }
                                label={opt.label}
                            />
                        ))}
                    </Box>
                )}

                {step === 3 && (
                    <Box>
                        {executing ? (
                            <Box sx={{ mb: 2 }}>
                                <LinearProgress variant="determinate" value={rows.filter((r) => r.selected).length ? (progress / rows.filter((r) => r.selected).length) * 100 : 0} />
                                <Typography variant="body2" sx={{ mt: 1 }}>
                                    מבצע... {progress} / {rows.filter((r) => r.selected).length}
                                </Typography>
                            </Box>
                        ) : (
                            <Alert severity={errorCount > 0 ? 'warning' : 'success'} sx={{ mb: 2 }}>
                                הסתיים: {successCount} הצליחו, {errorCount} נכשלו.
                            </Alert>
                        )}
                        <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 420 }}>
                            <Table size="small" stickyHeader>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>שורה</TableCell>
                                        <TableCell>{adapter.entityLabel}</TableCell>
                                        <TableCell>פעולה</TableCell>
                                        <TableCell>סטטוס</TableCell>
                                        <TableCell>הודעה</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {results.map((r, i) => (
                                        <TableRow key={i}>
                                            <TableCell>{r.line}</TableCell>
                                            <TableCell>{r.label}</TableCell>
                                            <TableCell>{ACTION_META[r.action]?.label ?? r.action}</TableCell>
                                            <TableCell>
                                                {r.status === 'success' ? (
                                                    <Chip size="small" color="success" icon={<CheckCircleIcon fontSize="small" />} label="הצליח" />
                                                ) : r.status === 'error' ? (
                                                    <Chip size="small" color="error" icon={<ErrorIcon fontSize="small" />} label="נכשל" />
                                                ) : (
                                                    <Chip size="small" label="דולג" />
                                                )}
                                            </TableCell>
                                            <TableCell>{r.message}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Box>
                )}
            </DialogContent>
            <DialogActions>
                {step === 0 && (
                    <>
                        <Button onClick={handleClose}>ביטול</Button>
                        <Button variant="contained" disabled={!file || rawRows.length === 0} onClick={() => setStep(1)} startIcon={<CloudUploadIcon />}>
                            הבא — התאמת עמודות
                        </Button>
                    </>
                )}
                {step === 1 && (
                    <>
                        <Button onClick={() => setStep(0)}>חזרה</Button>
                        <Button variant="contained" disabled={!canProceedFromMapping} onClick={goToReview}>
                            הבא — בדיקה
                        </Button>
                    </>
                )}
                {step === 2 && (
                    <>
                        <Button onClick={() => setStep(1)}>חזרה למיפוי</Button>
                        <Button variant="contained" color="success" disabled={!approved || !canRun} onClick={handleApproveAndRun}>
                            ביצוע היבוא ({selectedCount})
                        </Button>
                    </>
                )}
                {step === 3 && (
                    <>
                        {!executing && errorCount > 0 ? (
                            <Button startIcon={<ReplayIcon />} onClick={handleRetryFailed}>
                                ניסיון חזרה על השורות שנכשלו
                            </Button>
                        ) : null}
                        <Button startIcon={<DownloadIcon />} disabled={results.length === 0} onClick={() => downloadResultsCsv(results, `תוצאות יבוא - ${adapter.title}.csv`)}>
                            הורדת תוצאות
                        </Button>
                        <Button variant="contained" disabled={executing} onClick={handleFinishedAndClose}>
                            סגור ורענן
                        </Button>
                    </>
                )}
            </DialogActions>
        </Dialog>
    );
}

/** Small helper adapters commonly need: read a cell as a trimmed string, and as an int id. */
export function readIdentityCell(row: unknown[], cols: Record<string, number>, key: string): { text: string; num?: number } {
    const text = getCell(row, cols, key);
    return { text, num: toInt(text) };
}
